import axios from 'axios'

import type { LatLng } from '@/components/growers/area-map/use-area-polygon'
// Lấy base URL thẳng từ config chứ không qua `./client`: module này cố tình
// KHÔNG đụng tới axios client đã gắn interceptor đăng nhập — trang công khai
// không có tài khoản để làm mới token.
import { API_BASE_URL } from '@/lib/config'

/**
 * Vùng trồng nhìn từ trang công khai mà mã QR trỏ tới.
 *
 * Cố ý KHÔNG có tên thường gọi của hộ, grower_id, số điện thoại, địa chỉ, CCCD
 * hay tài khoản ngân hàng: trang này ai quét QR cũng mở được. Thêm trường vào
 * đây là phơi thêm dữ liệu ra Internet, không phải một thay đổi hiển thị.
 */
export interface PublicGrowerArea {
    area_id: string
    /** Mã hiển thị "MVTNNN" do hệ thống cấp. */
    code: string
    name: string
    /** Diện tích, đơn vị m². */
    acreage: number
    /** Tên khai sinh của hộ trồng. */
    grower_name: string
    /** Đỉnh ranh giới thửa; mảng rỗng khi vùng chưa khoanh toạ độ. */
    paths: LatLng[]
}

/**
 * Đường dẫn công khai của một vùng trồng — cũng chính là nội dung mã QR.
 *
 * Dùng `window.location.origin` chứ không phải một biến môi trường riêng: app
 * chạy trên nhiều tên miền (LAN khi dev, staging, production) và QR phải trỏ về
 * đúng nơi người dùng đang mở, nếu không nhân viên in QR trên staging là khách
 * quét ra trang staging.
 */
export function publicGrowerAreaPath(areaId: string): string {
    return `/grower-areas/${areaId}`
}

export function publicGrowerAreaUrl(areaId: string): string {
    const origin =
        typeof window === 'undefined' ? '' : window.location.origin.trim()
    return `${origin}${publicGrowerAreaPath(areaId)}`
}

/**
 * Endpoint công khai — không cần cookie đăng nhập. Dùng axios thuần (không phải
 * `api` client) để bỏ qua interceptor đẩy về `/login` khi gặp 401: người quét QR
 * là khách hàng, không có tài khoản.
 *
 * Trả `null` cho 404 (không có vùng nào mang id đó) VÀ 400 (id không phải UUID):
 * cả hai đều là "quét phải mã QR hỏng hoặc đã cũ", khác hẳn hỏng mạng — ném 400
 * lên thì trang báo "kiểm tra kết nối" và người quét đi bật lại wifi vô ích.
 *
 * `areaId` đi thẳng từ đường dẫn nên phải mã hóa: một mã QR bịa dạng
 * `../../auth/me` mà ghép thô là biến lời gọi công khai thành lời gọi vào API khác.
 */
export async function fetchPublicGrowerArea(
    areaId: string,
): Promise<PublicGrowerArea | null> {
    try {
        const res = await axios.get<{
            success: boolean
            data?: PublicGrowerArea
        }>(`${API_BASE_URL}/public/grower-areas/${encodeURIComponent(areaId)}`)
        return res.data?.data ?? null
    } catch (err) {
        const status = axios.isAxiosError(err)
            ? err.response?.status
            : undefined
        if (status === 404 || status === 400) {
            return null
        }
        throw err
    }
}
